import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import { ApplicantColleges, FormNames } from '@youscience/commonapp-service-common';
import { useEffect, useState } from 'react';
import { FormMetadata } from '../components/common/interfaces';
import { FormCompletionStatus } from '../components/common/enums';
import { get, set, isEmpty, isEqual } from 'lodash-es';
import { getFormsMetadata } from '../components/common/formUtils';

export interface ApplicantForm<T extends object> {
  content?: T;
  metadata: FormMetadata;
  sectionIsSaved: Record<keyof T, boolean>;
  isReady: boolean;
  isError: boolean;
  isSavingForm: boolean;
  isLoadingPreview: boolean;
  previewPdfURL: string | undefined;
  save: (form: Partial<T>) => void;
  preview: (form: Partial<T>) => void;
  submit: (form: Partial<T>) => void;
  getSubmitted: (form: Partial<T>) => void;
  unsubmit: (form: Partial<T>) => void;
}

const getNewContent = <T extends object>(applicant: ApplicantColleges, name: FormNames): T => {
  let newContent = {} as T;
  if (name === FormNames.TEACHER_EVALUATION) {
    newContent = {
      ...(applicant?.forms[name]?.find((form) => form.recommenderId === applicant.teacherRecommenderId) || {}),
      applicantId: applicant.applicantId,
      recommenderId: applicant.teacherRecommenderId,
    } as T;
  } else {
    newContent = {
      ...(applicant?.forms[name] || {}),
      applicantId: applicant.applicantId,
      recommenderId: applicant.counselorRecommenderId,
    } as T;
  }

  return newContent;
};

const getNewSectionIsSaved = <T extends object>(newContent: T, oldContent?: T): Record<keyof T, boolean> => {
  const newSectionIsSaved = {} as Record<keyof T, boolean>;
  const metadataFields = ['applicantId', 'recommenderId'];

  for (const section of Object.keys(newContent) as (keyof T)[]) {
    if (metadataFields.includes(section as string)) {
      continue;
    }

    if (oldContent?.[section] && !isEqual(oldContent[section], newContent[section])) {
      newSectionIsSaved[section] = true;
    } else if (!oldContent?.[section] && newContent[section]) {
      newSectionIsSaved[section] = true;
    }
  }

  return newSectionIsSaved;
};

export const useApplicantForm = <T extends object>(name: FormNames): ApplicantForm<T> => {
  const {
    applicant,
    isError,
    isSavingForm,
    isLoadingPreview,
    previewPdfURL,
    saveForm,
    previewForm,
    submitForm,
    unsubmitForm,
    getSubmittedForm,
  } = useRecommenderWorkspaceStore();

  const [form, setForm] = useState<ApplicantForm<T>>({
    isReady: false,
    metadata: { applicantId: 0, recommenderId: 0, name, members: [] },
    sectionIsSaved: {} as Record<keyof T, boolean>,
    isError,
    isSavingForm,
    isLoadingPreview,
    previewPdfURL,
    save: (form) => saveForm(form!, name),
    preview: (form) => previewForm(form!, name),
    submit: (form) => submitForm(form!, name),
    unsubmit: (form) => unsubmitForm(form!, name),
    getSubmitted: (form) => getSubmittedForm(form!, name),
  });

  useEffect(() => {
    if (!applicant) {
      return;
    }

    if (isSavingForm) {
      setForm((prevForm) => ({
        ...prevForm,
        isSavingForm,
        isLoadingPreview,
        isError,
        previewPdfURL,
        sectionIsSaved: {} as Record<keyof T, boolean>,
        save: (form) => saveForm(form!, name),
        preview: (form) => previewForm(form!, name),
        submit: (form) => submitForm(form!, name),
        unsubmit: (form) => unsubmitForm(form!, name),
        getSubmitted: (form) => getSubmittedForm(form!, name),
      }));
    } else {
      const newContent = getNewContent<T>(applicant, name);
      const newSectionIsSaved = getNewSectionIsSaved<T>(newContent, form.content);
      const newMetadata = getFormsMetadata(applicant)[name] || { applicantId: 0, recommenderId: 0, name };

      if (newMetadata?.completionStatus === FormCompletionStatus.NEW && Object.keys(newContent).length > 2) {
        newMetadata.completionStatus = FormCompletionStatus.IN_PROGRESS;
      }

      setForm((prevForm) => ({
        ...prevForm,
        content: { ...newContent },
        metadata: { ...newMetadata },
        isReady: !!newMetadata.applicantId,
        sectionIsSaved: { ...prevForm.sectionIsSaved, ...newSectionIsSaved },
        isError,
        isSavingForm,
        isLoadingPreview,
        previewPdfURL,
        save: (form) => saveForm(form!, name),
        preview: (form) => previewForm(form!, name),
        submit: (form) => submitForm(form!, name),
        unsubmit: (form) => unsubmitForm(form!, name),
        getSubmitted: (form) => getSubmittedForm(form!, name),
      }));
    }
  }, [applicant, name, isSavingForm, isLoadingPreview, isError, previewPdfURL]);

  return {
    ...form,
  };
};
